import { useState, useEffect, useMemo, useRef } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import EditModal from '../ui/EditModal';
import FiltrosBusqueda from './FiltrosBusqueda';
import TablaPersonas from './TablaPersonas';
import ContextoFiltros from './ContextoFiltros';
import { createMatcher, itemMatchesPatologia, itemMatchesZona } from './utils/filtros';
import { usePatologia } from "../../context/PatologiaContext";
import { useZona } from "../../context/ZonaContext";
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ListadoCasosContactos.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix icono Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function MapClickHandler({ onSelect }) {
  useMapEvents({
    click: async (e) => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${e.latlng.lat}&lon=${e.latlng.lng}&format=json`
        );
        const data = await res.json();
        const addr = data.display_name || `${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`;
        onSelect({ addr, lat: e.latlng.lat, lng: e.latlng.lng });
      } catch {
        onSelect({ addr: `${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`, lat: e.latlng.lat, lng: e.latlng.lng });
      }
    }
  });
  return null;
}

const DomicilioMapPicker = ({ inicial, onChange }) => {
  const [dirString, setDirString] = useState(typeof inicial === 'string' ? inicial : '');
  const [mapaAbierto, setMapaAbierto] = useState(false);
  const [coordenadas, setCoordenadas] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setDirString(typeof inicial === 'string' ? inicial : '');
  }, [inicial]);

  const buscarSugerencias = (q) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!q || q.length < 3) { setSugerencias([]); return; }
    setBuscando(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`);
        const data = await res.json();
        setSugerencias(data);
      } catch { setSugerencias([]); }
      setBuscando(false);
    }, 400);
  };

  const seleccionarSugerencia = (sug) => {
    const addr = sug.display_name;
    setDirString(addr);
    setCoordenadas({ lat: parseFloat(sug.lat), lng: parseFloat(sug.lon) });
    setSugerencias([]);
    onChange(addr);
  };

  const handleMapSelect = ({ addr, lat, lng }) => {
    setDirString(addr);
    setCoordenadas({ lat, lng });
    onChange(addr);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          className="input"
          type="text"
          value={dirString}
          onChange={(e) => {
            setDirString(e.target.value);
            buscarSugerencias(e.target.value);
            onChange(e.target.value);
          }}
          placeholder="Escribí o seleccioná en el mapa..."
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className={`button is-small ${mapaAbierto ? 'is-light' : 'is-info'}`}
          onClick={() => setMapaAbierto(v => !v)}
        >
          {mapaAbierto ? 'Cerrar mapa' : 'Abrir mapa'}
        </button>
      </div>

      {sugerencias.length > 0 && (
        <ul style={{
          border: '1px solid #ddd', borderRadius: 4, background: '#fff',
          position: 'relative', zIndex: 1000, margin: 0, padding: 0,
          listStyle: 'none', maxHeight: 140, overflowY: 'auto', boxShadow: '0 2px 6px #0001'
        }}>
          {sugerencias.map((s, i) => (
            <li
              key={i}
              style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.82rem' }}
              onMouseDown={() => seleccionarSugerencia(s)}
            >
              {s.display_name}
            </li>
          ))}
        </ul>
      )}

      {mapaAbierto && (
        <div style={{ marginTop: '0.5rem', height: 280, borderRadius: 8, overflow: 'hidden', border: '1px solid #dce5ec' }}>
          <MapContainer
            center={coordenadas ? [coordenadas.lat, coordenadas.lng] : [-34.6037, -58.3816]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onSelect={handleMapSelect} />
            {coordenadas && <Marker position={[coordenadas.lat, coordenadas.lng]} />}
          </MapContainer>
        </div>
      )}
      {mapaAbierto && (
        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.25rem' }}>
          Clic en el mapa para seleccionar la ubicación
        </p>
      )}
    </div>
  );
};

const DiagnosticoCheckboxes = ({ inicial, onChange, opciones }) => {
  const [seleccionados, setSeleccionados] = useState(Array.isArray(inicial) ? inicial : []);

  useEffect(() => {
    setSeleccionados(Array.isArray(inicial) ? inicial : []);
  }, [inicial]);

  const toggle = (id) => {
    const next = seleccionados.includes(id)
      ? seleccionados.filter(x => x !== id)
      : [...seleccionados, id];
    setSeleccionados(next);
    onChange(next);
  };

  if (!opciones || opciones.length === 0) {
    return <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Sin diagnósticos para la patología activa</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '150px', overflowY: 'auto' }}>
      {opciones.map(d => {
        const id = Number(d.IdDiagnostico);
        return (
          <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', fontSize: '0.95rem' }}>
            <input type="checkbox" checked={seleccionados.includes(id)} onChange={() => toggle(id)} style={{ accentColor: '#2d7a9b' }} />
            <span>{d.NombreDiagnostico || d.Nombre || `Diagnóstico ${id}`}</span>
          </label>
        );
      })}
    </div>
  );
};

const normalizarEstadoActivo = (persona) => {
  const estado = persona?.estado ?? persona?.Estado ?? persona?.EstadoPersona;
  return Number(estado ?? 1);
};

const normalizarPersonaListado = (persona) => ({
  ...persona,
  Nombre: persona?.Nombre ?? persona?.NombrePersona ?? "",
  Apellido: persona?.Apellido ?? "",
  NroDocumento: persona?.NroDocumento ?? "",
  Domicilio: persona?.Domicilio ?? "",
  Telefono: persona?.Telefono ?? "",
  Eventos: persona?.Eventos ?? "",
  Diagnostico: persona?.Diagnostico ?? persona?.Diagnosticos ?? "",
  Edad: persona?.Edad ?? "",
  ClasificacionInicioTratamiento: persona?.ClasificacionInicioTratamiento ?? "",
  ResultadoTratamiento: persona?.ResultadoTratamiento ?? "",
  Sexo: persona?.Sexo ?? "",
  Genero: persona?.Genero ?? "",
  tipo: persona?.tipo ?? persona?.Tipo ?? "",
  estado: normalizarEstadoActivo(persona),
});

export default function ListadoCasosContactos() {
  // ---------- ESTADOS PRINCIPALES ----------
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [filterText, setFilterText] = useState(sessionStorage.getItem("searchTermPersona") || "");
  const [selectedEventos, setSelectedEventos] = useState([]);
  const [rowDense, setRowDense] = useState(true);
  const [fechasSeguimientoFinales, setFechasSeguimientoFinales] = useState([]);
  const [personaSeleccionada, setPersonaSeleccionada] = useState(null);
  const [activeTab, setActiveTab] = useState("Todos");
  const [activoFiltro, setActivoFiltro] = useState("Todos");
  const [estados, setEstados] = useState([]);
  const [estadosTratamiento, setEstadosTratamiento] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [diagnosticosDisponibles, setDiagnosticosDisponibles] = useState([]);
  // ---------- DATOS DEL USUARIO Y CONTEXTOS ----------
  const idUsuario = sessionStorage.getItem("idUsuario");
  const { patologiaActiva } = usePatologia();
  const { zonaActiva } = useZona();

  const patologiaActivaId = useMemo(
    () => localStorage.getItem("idPatologiaSeleccionada"),
    [patologiaActiva]
  );
  const zonaActivaId = useMemo(
    () => localStorage.getItem("idZonaActiva"),
    [zonaActiva]
  );

  // ------------------------------------------------------
  // EFECTOS DE CARGA INICIAL
  // ------------------------------------------------------
  useEffect(() => {
    cargarTiposDocumento();
    cargarEstados();
  }, []);

  
// Recargar datos cuando cambia patología o zona
useEffect(() => {
  cargarDatosCompletos();
}, [patologiaActiva, zonaActiva, patologiaActivaId, zonaActivaId]);

// 🔥 Recargar estados cuando la patología cambia
useEffect(() => {
  cargarEstados();
}, [patologiaActiva, patologiaActivaId]);

  useEffect(() => {
    cargarDatosCompletos();
  }, [patologiaActiva, zonaActiva, patologiaActivaId, zonaActivaId]);

  // ------------------------------------------------------
  // FUNCIONES DE CARGA DE DATOS
  // ------------------------------------------------------
  const cargarTiposDocumento = async () => {
    try {
      const response = await api.get("/tipodocumentos");
      setTiposDocumento(response.data);
    } catch (error) {
      console.error("Error al cargar tipos de documento:", error);
    }
  };

  const cargarEstados = async () => {
  try {
    const patologiaId = localStorage.getItem("idPatologiaSeleccionada");

    if (!patologiaId) {
      console.warn("No hay patología seleccionada, no se cargarán estados");
      setEstados([]);
      setEstadosTratamiento([]);
      return;
    }

    const response = await api.get(
      "/estadosporpatologia/" + patologiaId
    );

    // Normalizar: asegurar estructura uniforme
    const estadosNormalizados = response.data.map(e => ({
      IdEstadoPorPatologia: e.IdEstadoPorPatologia,
      IdEstado: e.IdEstado,
      Nombre: e.Nombre,
      ColorAsociado: e.ColorAsociado
    }));

    setEstados(estadosNormalizados);

    const responseTratamiento = await api.get(
      "/estados-tratamiento-por-patologia/" + patologiaId
    );
    // Se incluyen también relaciones inactivas para conservar el color de
    // los valores históricos ya asignados a personas.
    setEstadosTratamiento(responseTratamiento.data);

  } catch (error) {
    console.error("Error al cargar estados por patología:", error);
    setEstadosTratamiento([]);
  }
};

  const cargarDatosCompletos = async () => {
    try {
      const params = {};

      const patologiaNombre = typeof patologiaActiva === "object"
        ? patologiaActiva?.Nombre || patologiaActiva?.nombre
        : patologiaActiva;
      const zonaNombre = typeof zonaActiva === "object"
        ? zonaActiva?.Nombre || zonaActiva?.nombre
        : zonaActiva;

      if (patologiaActivaId) params.idPatologia = patologiaActivaId;
      else if (patologiaNombre) params.patologia = patologiaNombre;

      if (zonaActivaId) params.idZonaSanitaria = zonaActivaId;
      else if (zonaNombre) params.zona = zonaNombre;

      let casosData = [];
      try {
        const responseCasos = await api.get(
          "/clasificaciones-personas",
          { params }
        );
        casosData = Array.isArray(responseCasos.data) ? responseCasos.data : [];
      } catch (error) {
        console.error("Error al cargar casos filtrados:", error);
      }

      let contactosData = [];
      try {
        const responseContactos = await api.get("/personas-tipo", { params });
        contactosData = Array.isArray(responseContactos.data) ? responseContactos.data : [];
      } catch (error) {
        console.error("Error al cargar contactos:", error);
      }

      let estadosMap = new Map();
      try {
        const estadosResponse = await api.get("/estadosporpersona");
        estadosMap = new Map(estadosResponse.data.map(e => [e.IdPersona, e.IdEstado]));
      } catch (err) {
        console.warn("No se pudieron traer estados por persona:", err);
      }

      const personasProcesadas = await procesarYCombinarDatos(casosData, contactosData, estadosMap);
      setPersonas(personasProcesadas);

      // Cargar fechas de seguimiento para todas las personas
      await cargarFechasSeguimiento(personasProcesadas);

    } catch (error) {
      console.error("Error en cargarDatosCompletos:", error);
    }
  };

  const cargarFechasSeguimiento = async (personas) => {
    try {
      const fechasMap = new Map();
      
      await Promise.all(
        personas.map(async (persona) => {
          try {
            const response = await api.get(`/fechasPorPersonaSeleccionada/${persona.IdPersona}`);
            if (response.data && response.data.length > 0) {
              fechasMap.set(persona.IdPersona, {
                IdPersona: persona.IdPersona,
                FechaProbableExposicion: response.data[0].FechaProbableExposicion,
                FechaInicioSintomas: response.data[0].FechaInicioSintomas,
                FechaPrimeraConsulta: response.data[0].FechaPrimeraConsulta
              });
            }
          } catch (error) {
            console.warn(`No se pudieron cargar fechas para persona ${persona.IdPersona}:`, error);
          }
        })
      );

      setFechasSeguimientoFinales(Array.from(fechasMap.values()));
    } catch (error) {
      console.error("Error al cargar fechas de seguimiento:", error);
    }
  };

  const procesarYCombinarDatos = async (casosData, contactosData, estadosMap) => {
    const personasMap = new Map();

    // Use contactosData as the base list of all people.
    for (const p of contactosData) {
      const personaNormalizada = normalizarPersonaListado(p);
      personasMap.set(p.IdPersona, {
        ...personaNormalizada,
        IdEstado: estadosMap.get(p.IdPersona) || p.IdEstado || 1,
      });
    }

    // If we have filtered classification data, update the map with it.
    // This adds the pathology/zone properties needed for frontend filtering.
    if (casosData.length > 0) {
      for (const caso of casosData) {
        if (personasMap.has(caso.IdPersona)) {
          const persona = personasMap.get(caso.IdPersona);
          Object.assign(persona, caso); // Merge properties from casoData
          Object.assign(persona, normalizarPersonaListado(persona));
        } else {
          // This case was not in the general list, add it.
          const casoNormalizado = normalizarPersonaListado(caso);
          personasMap.set(caso.IdPersona, {
            ...casoNormalizado,
            IdEstado: estadosMap.get(caso.IdPersona) || caso.IdEstado || 1,
          });
        }
      }
    }

    const personasConClasificacion = Array.from(personasMap.values());

    // Now, enrich with sub-row data (casos/contactos)
    const personasEnriquecidas = await Promise.all(
      personasConClasificacion.map(async persona => {
        try {
          const res = await api.get("/casos/" + persona.IdPersona);
          const relatedPeople = Array.isArray(res.data?.casos) ? res.data.casos : [];

          if (persona.tipo === 'Caso') {
            persona.contactos = relatedPeople;
            persona.casos = [];
          } else { // Contacto
            persona.casos = relatedPeople;
            persona.contactos = [];
          }
        } catch {
          persona.casos = [];
          persona.contactos = [];
        }
        return persona;
      })
    );

    return personasEnriquecidas;
  }

  // ------------------------------------------------------
  // HANDLERS
  // ------------------------------------------------------
  const handleEstadoChange = async (idPersona, nuevoEstado, parentId = null) => {
    try {
      // Obtener el nombre del estado seleccionado
      const estadoSeleccionado = estados.find(e => e.IdEstado === nuevoEstado);
      const nombreEstado = estadoSeleccionado?.Nombre || "este estado";

      // Mostrar confirmación
      const result = await Swal.fire({
        title: '¿Confirmar cambio de estado?',
        html: `¿Estás seguro de cambiar el estado a <strong>${nombreEstado}</strong>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, cambiar',
        cancelButtonText: 'Cancelar'
      });

      // Si el usuario cancela, no hacer nada
      if (!result.isConfirmed) {
        return null;
      }

      const response = await api.put(
        `/personas/${idPersona}/estado`,
        { IdEstado: nuevoEstado },
        { headers: { "Content-Type": "application/json" } }
      );

      const nuevoId = response.data?.estado?.IdEstado ?? nuevoEstado;

      setPersonas(prev =>
        prev.map(p =>
          p.IdPersona === idPersona
            ? { ...p, IdEstado: nuevoId }
            : p
        )
      );
      
      // Mostrar mensaje de éxito
      await Swal.fire({
        icon: 'success',
        title: 'Estado actualizado',
        text: `El estado fue cambiado a ${nombreEstado}`,
        timer: 2000,
        showConfirmButton: false
      });
      
      return nuevoId;
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cambiar el estado. Inténtalo nuevamente.'
      });
      return null;
    }
  };

  const handleEstadoTratamientoChange = async (idPersona, campo, idRelacion) => {
    const estado = estadosTratamiento.find((item) =>
      Number(item.IdEstadoTratamientoPorPatologia) === Number(idRelacion)
    );
    const etiquetaCampo = campo === "clasificacion"
      ? "Clasificación inicio tratamiento"
      : "Resultado del tratamiento";
    const valor = estado?.Nombre || null;

    try {
      const confirmacion = await Swal.fire({
        title: `¿Cambiar ${etiquetaCampo.toLowerCase()}?`,
        html: valor ? `Se asignará <strong>${valor}</strong>.` : "Se dejará sin asignar.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, cambiar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacion.isConfirmed) return null;

      const response = await api.patch(`/personas/${idPersona}/estado-tratamiento`, {
        campo,
        IdEstadoTratamientoPorPatologia: idRelacion || null,
      });
      const propiedad = campo === "clasificacion" ? "ClasificacionInicioTratamiento" : "ResultadoTratamiento";
      setPersonas((prev) => prev.map((persona) =>
        persona.IdPersona === idPersona
          ? { ...persona, [propiedad]: response.data.valor }
          : persona
      ));
      return response.data.valor;
    } catch (error) {
      console.error("Error al cambiar estado de tratamiento:", error);
      Swal.fire("Error", error.response?.data?.message || "No se pudo actualizar el estado de tratamiento.", "error");
      return null;
    }
  };

  const handleActivarDesactivar = async (IdPersona, nuevoEstado) => {
    try {
      const res = await api.patch(
        "/personas/cambiarEstado/" + IdPersona,
        { IdEstado: nuevoEstado }
      );

      const estadoReal = res.data.estado;
      setPersonas(prev =>
        prev.map(p =>
          p.IdPersona === IdPersona ? { ...p, estado: estadoReal } : p
        )
      );
    } catch (error) {
      console.error("Error al cambiar estado:", error);
    }
  };

  const handleSubmitEdit = async (valoresActualizados) => {
    try {
      const { Domicilio, Diagnosticos, Diagnostico, ...datosPersona } = valoresActualizados;

      await api.put(
        "/" + datosPersona.IdPersona,
        datosPersona,
        { headers: { "Content-Type": "application/json" } }
      );

      if (typeof Domicilio !== 'undefined' && Domicilio !== null) {
        await api.patch(`/${datosPersona.IdPersona}/domicilio`, { Domicilio });
      }

      const patId = patologiaActivaId || localStorage.getItem("idPatologiaSeleccionada");
      if (Array.isArray(Diagnosticos) && patId) {
        await api.post('diagnosticosxpersonaxpatologia', {
          IdPersona: Number(datosPersona.IdPersona),
          IdPatologia: Number(patId),
          diagnosticos: Diagnosticos.map(Number)
        });
      }

      await cargarDatosCompletos();
      closeModal();
    } catch (error) {
      console.error("Error al actualizar persona:", error);
      alert("Hubo un error al actualizar la persona");
    }
  };

  // ---------------- MODAL HANDLERS ----------------
  const openModal = async (persona) => {
    setPersonaSeleccionada(persona);
    setModalIsOpen(true);
    const patId = patologiaActivaId || localStorage.getItem("idPatologiaSeleccionada");
    if (patId) {
      try {
        const [disponiblesRes, asignadosRes] = await Promise.all([
          api.get(`diagnosticosPorPatologia/${patId}`),
          api.get(`diagnosticosxpersonaxpatologia/${persona.IdPersona}/${patId}`)
        ]);
        const disponibles = disponiblesRes.data.filter(d => Number(d.estado) === 1);
        setDiagnosticosDisponibles(disponibles);
        const ids = asignadosRes.data.map(d => Number(d.IdDiagnostico));
        setPersonaSeleccionada({ ...persona, Diagnosticos: ids });
      } catch (err) {
        console.error("Error cargando diagnósticos para modal:", err);
        setDiagnosticosDisponibles([]);
      }
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setPersonaSeleccionada(null);
  };

  // ---------------- FILTROS Y DATOS PROCESADOS ----------------
  const patologiaNombreContexto =
    typeof patologiaActiva === "object"
      ? patologiaActiva?.Nombre || patologiaActiva?.nombre
      : patologiaActiva;

  const zonaNombreContexto =
    typeof zonaActiva === "object"
      ? zonaActiva?.Nombre || zonaActiva?.nombre
      : zonaActiva;

  const patologiaMatcher = createMatcher(patologiaNombreContexto, patologiaActivaId);
  const zonaMatcher = createMatcher(zonaNombreContexto, zonaActivaId);

  const normalizarEvento = (value) =>
    value
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const limpiarEvento = (value) =>
    value
      .toString()
      .replace(/\s+/g, " ")
      .trim();
  const extraerEventos = (eventosTexto) => {
    if (!eventosTexto) return [];
    return eventosTexto
      .toString()
      .split(/[\n,;|]+/)
      .map(limpiarEvento)
      .filter(Boolean);
  };

  const eventoOptions = useMemo(() => {
    const unicos = new Map();
    personas.forEach((persona) => {
      extraerEventos(persona.Eventos).forEach((ev) => {
        const clave = normalizarEvento(ev);
        if (clave && !unicos.has(clave)) {
          unicos.set(clave, ev);
        }
      });
    });
    return Array.from(unicos.values()).sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
  }, [personas]);

  const personasFiltradas = personas
    .filter(item => {
      if (activeTab === "Todos") return true;
      if (activeTab === "Casos") return item.tipo === "Caso";
      if (activeTab === "Contactos") return item.tipo === "Contacto";
      return true;
    })
    .filter(item => {
      const estadoSeleccionadoObj = estados.find(e => e.IdEstado === Number(estadoSeleccionado));
      const coincideEstado = estadoSeleccionadoObj
        ? item.IdEstado === estadoSeleccionadoObj.IdEstado
        : true;
      return coincideEstado;
    })
    .filter(item => {
      if (activoFiltro === "Activos") return item.estado === 1 || item.estado === "1";
      if (activoFiltro === "Inactivos") return item.estado !== 1 && item.estado !== "1";
      return true;
    })
    .filter(item => {
      if (selectedEventos.length === 0) return true;
      const eventosPersonaNormalizados = new Set(
        extraerEventos(item.Eventos).map(normalizarEvento).filter(Boolean)
      );
      return selectedEventos.some((eventoSel) => eventosPersonaNormalizados.has(normalizarEvento(eventoSel)));
    })
    .filter(item => itemMatchesPatologia(item, patologiaMatcher))
    .filter(item => itemMatchesZona(item, zonaMatcher));

  const toggleEvento = (evento) => {
    setSelectedEventos((prev) =>
      prev.includes(evento)
        ? prev.filter((ev) => ev !== evento)
        : [...prev, evento]
    );
  };

  const clearEventos = () => setSelectedEventos([]);

  // ---------------- campos para modal ----------------
  const camposEdicion = [
    { name: "Nombre", label: "Nombre", type: "text", required: true },
    { name: "Apellido", label: "Apellido", type: "text", required: true },
    { name: "NroDocumento", label: "Número de Documento", type: "text", required: true },
    {
      name: "IdTipoDocumento",
      label: "Tipo de Documento",
      type: "select",
      required: true,
      options: tiposDocumento.map(t => ({
        value: t.IdTipoDocumento || t.idTipoDocumento,
        label: t.TipoDocumento
      }))
    },
    { name: "Edad", label: "Edad", type: "number", required: true },
    {
      name: "Sexo",
      label: "Sexo",
      type: "select",
      required: true,
      options: [
        { value: "Masculino", label: "Masculino" },
        { value: "Femenino", label: "Femenino" },
        { value: "Otro", label: "Otro" }
      ]
    },
    {
      name: "Genero",
      label: "Género",
      type: "select",
      required: false,
      options: [
        { value: "Desconocido", label: "Desconocido" },
        { value: "Mujer cis", label: "Mujer cis" },
        { value: "Mujer trans", label: "Mujer trans" },
        { value: "Varón cis", label: "Varón cis" },
        { value: "Varón trans", label: "Varón trans" },
        { value: "Otro", label: "Otro" }
      ]
    },
    { name: "Telefono", label: "Teléfono", type: "text", required: false },
    { name: "Eventos", label: "Eventos", type: "textarea", required: false },
    { name: "Domicilio", label: "Domicilio", type: "custom", component: DomicilioMapPicker },
    {
      name: "Diagnosticos",
      label: "Diagnósticos",
      type: "custom",
      component: DiagnosticoCheckboxes,
      componentProps: { opciones: diagnosticosDisponibles }
    },
  ];

  return (
    <div className="columns is-gapless listado-casos-contactos">
      {/* <div className="column is-centered" style={{ marginTop: "2vh" }}></div> */}
      <div className="column is-centered listado-casos-contactos__contenido">
        <FiltrosBusqueda
          filterText={filterText}
          onFilterChange={setFilterText}
          eventoOptions={eventoOptions}
          selectedEventos={selectedEventos}
          onToggleEvento={toggleEvento}
          onClearEventos={clearEventos}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          estadoSeleccionado={estadoSeleccionado}
          onEstadoChange={setEstadoSeleccionado}
          estados={estados}
          activoFiltro={activoFiltro}
          onActivoFiltroChange={setActivoFiltro}
        />

        <ContextoFiltros
          patologiaNombre={patologiaNombreContexto}
          zonaNombre={zonaNombreContexto}
        />

        <TablaPersonas
          personas={personasFiltradas}
            personasFull={personas}   // 👈 NUEVO

          tiposDocumento={tiposDocumento}
          estados={estados}
          estadosTratamiento={estadosTratamiento}
          fechasSeguimientoFinales={fechasSeguimientoFinales}
          onEstadoChange={handleEstadoChange}
          onEstadoTratamientoChange={handleEstadoTratamientoChange}
          onOpenModal={openModal}
          activarDesactivarPersona={handleActivarDesactivar}
          expandedRows={expandedRows}
          onRowClick={(id) => setExpandedRows(prev =>
            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
          )}
          filterText={filterText}
          rowDense={rowDense}
        />

        {modalIsOpen && personaSeleccionada && (
          <EditModal
            fields={camposEdicion}
            initialValues={personaSeleccionada}
            onSubmit={handleSubmitEdit}
            title="Editar Persona"
            onClose={closeModal}
          />
        )}
      </div>
    </div>
  );
}
